import React, { useEffect, useState } from 'react';
import { getCsrfToken } from '../utils/auth';
import { updateDevkit } from '../utils/updateDevkit';

const channelMap = {
    Stable: 'release',
    Nightly: 'testing',
    Experimental: 'sandbox',
};

const reverseChannelMap = {
    release: 'Stable',
    testing: 'Nightly',
    sandbox: 'Experimental',
};

const BatchUpdatePanel = ({
    devkits = [],
    selectedDevkits = [],
    onClearSelection = () => {},
    admin = false,
    onToggleDevkitSelection,
}) => {
    const count = selectedDevkits.length;

    const [activeApp, setActiveApp] = useState(null);
    const [channel, setChannel] = useState('Stable');
    const [selectedVersion, setSelectedVersion] = useState('latest');
    const [availableUpdates, setAvailableUpdates] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [currentUpdatingDevice, setCurrentUpdatingDevice] = useState(null);
    const [activeUpdateStep, setActiveUpdateStep] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [updatedDevices, setUpdatedDevices] = useState([]);          // successes (persistent)
    const [failedDevices, setFailedDevices] = useState([]);            // [{ name, reason }]
    const [showResults, setShowResults] = useState(false);             // show summary modal


    const isSelected = (devkit) =>
        selectedDevkits.some((d) => d.devkit_id === devkit.devkit_id);

    const fetchAvailableVersions = async (app, chan) => {
        setAvailableUpdates([]);

        const res = await fetch('/api/available-versions?channel=' + channelMap[chan] + '&app=' + app, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-CSRF-TOKEN': getCsrfToken()
            }
        });
        if (!res.ok) return;
        const data = await res.json();

        const parseVersions = data.available_versions
            .map((v) => {
                const match = v.match(/_(\d+\.\d+\.\d+)_([\d]+)_?/);
                return match ? `${match[1]} (${match[2]})` : null;
            })
            .filter(Boolean);

        setAvailableUpdates(parseVersions);
    };

    const handleAppClick = (app) => {
        setActiveApp(app);
        setChannel('Stable');
        setSelectedVersion('latest');
        fetchAvailableVersions(app, 'Stable');
    };

    // Update logic is now in updateDevkit.js

    const sendBatchUpdate = async (app) => {
        setIsSending(true);
        setUpdatedDevices([]);
        setFailedDevices([]);
        setShowResults(false);

        const [version, build] =
            selectedVersion === 'latest'
            ? ['latest', undefined]
            : selectedVersion.match(/^([\d.]+) \((\d+)\)$/).slice(1);

        const updateBody = build ? { version, build: Number(build) } : { version };

        for (const devkit of selectedDevkits) {
            const name = devkit.devkit_id;
            const host = devkit.tailscale?.ipv4?.[0];
            if (!host) {
                setFailedDevices(prev => [...prev, { name, reason: 'No IP available' }]);
                continue;
            }
            setCurrentUpdatingDevice(name);
            setActiveUpdateStep('Starting update...');
            try {
                await updateDevkit({
                    host,
                    app,
                    devkitName: name,
                    updateBody,
                    onStep: (step) => setActiveUpdateStep(step || ''),
                });
                setUpdatedDevices(prev => [...prev, name]);
            } catch (err) {
                setFailedDevices(prev => [...prev, { name, reason: err?.message || 'Unknown error' }]);
            }
        }

        setIsSending(false);
        setActiveApp(null);
        setShowResults(true);
    };

    const overallStatus = (() => {
        if (failedDevices.length === 0 && updatedDevices.length > 0) return 'success';
        if (failedDevices.length > 0 && updatedDevices.length > 0) return 'partial';
        if (failedDevices.length > 0 && updatedDevices.length === 0) return 'failure';
        return 'idle';
    })();

    return (
        <>
            <div className='w-full relative bg-gradient-to-br from-slate-50 to-blue-50/30 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-6 mb-8 shadow-xl shadow-slate-200/20'>
                {/* Subtle animated background pattern */}
                <div className='absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] rounded-2xl opacity-30'></div>
                
                {/* Header with expand/collapse toggle */}
                <div className='relative z-10 mb-6'>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className='w-full flex items-center justify-between p-4 -m-4 rounded-xl hover:bg-white/40 transition-all duration-200 group'
                    >
                        <div className='flex items-center gap-3'>
                            <div className={`w-3 h-3 rounded-full ${count > 0 ? 'bg-gradient-to-r from-emerald-400 to-teal-500 shadow-lg shadow-emerald-500/30' : 'bg-slate-300'} transition-all duration-300`}></div>
                            <h3 className='text-lg font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent'>
                                Batch Update Panel
                            </h3>
                            <div className='text-sm text-slate-500 font-medium'>
                                {count > 0 ? `${count} device${count > 1 ? 's' : ''} selected` : 'No devices selected'}
                            </div>
                        </div>
                        <div className={`w-8 h-8 rounded-full bg-slate-200/60 flex items-center justify-center transition-all duration-300 group-hover:bg-slate-300/60 ${isExpanded ? 'rotate-180' : ''}`}>
                            <svg className='w-4 h-4 text-slate-600' viewBox='0 0 16 16' fill='currentColor'>
                                <path fillRule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/>
                            </svg>
                        </div>
                    </button>
                </div>
                
                {/* Collapsible content */}
                {isExpanded && (
                    <div className='relative z-10 grid grid-cols-1 xl:grid-cols-2 gap-x-8 gap-y-2 animate-in slide-in-from-top-4 duration-300'>
                        {/* LEFT: Device selection + status */}
                        <div className='space-y-6'>
                            <div className='bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60 shadow-sm'>
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                                    {devkits.map((devkit) => (
                                        <label
                                            key={devkit.devkit_id}
                                            onClick={() => onToggleDevkitSelection(devkit)}
                                            className={`group relative flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
                                                isSelected(devkit)
                                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-md shadow-blue-200/40'
                                                : 'bg-white/80 border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                            }`}
                                        >
                                            <div
                                                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${
                                                isSelected(devkit)
                                                    ? 'bg-blue-500 border-blue-500'
                                                    : 'border-slate-300 bg-white'
                                                }`}
                                            >
                                                {isSelected(devkit) && (
                                                <svg
                                                    className="w-3 h-3 text-white"
                                                    viewBox="0 0 16 16"
                                                    fill="currentColor"
                                                >
                                                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                                </svg>
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <span className="font-mono text-sm font-medium text-slate-700 truncate block">
                                                {devkit.devkit_id}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <h2>Devkits must be online to appear in the list above</h2>

                            {isSending && currentUpdatingDevice && (
                                <div className='bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 shadow-sm'>
                                    <div className='flex items-center gap-3'>
                                        <div className='w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center'>
                                            <svg className='w-4 h-4 text-white animate-spin' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                                <circle cx='12' cy='12' r='10' className='opacity-25' />
                                                <path d='M4 12a8 8 0 018-8' className='opacity-75' />
                                            </svg>
                                        </div>
                                        <div className='flex-1'>
                                            <div className='font-semibold text-amber-800'>Updating {currentUpdatingDevice}</div>
                                            <div className='text-sm text-amber-600'>{activeUpdateStep || 'Preparing update...'}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Show successful updates (persist until summary) */}
                            {updatedDevices.map((deviceName) => (
                                <div key={deviceName} className='bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 shadow-sm animate-in fade-in-0 duration-300'>
                                    <div className='flex items-center gap-3'>
                                        <div className='w-8 h-8 rounded-full bg-gradient-to-r from-emerald-400 to-green-400 flex items-center justify-center'>
                                            <svg className='w-4 h-4 text-white' viewBox='0 0 16 16' fill='currentColor'>
                                                <path d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z'/>
                                            </svg>
                                        </div>
                                        <div className='flex-1'>
                                            <div className='font-semibold text-emerald-800'>Successfully updated {deviceName}</div>
                                            <div className='text-sm text-emerald-600'>Update completed</div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Show failures inline while running, if any */}
                            {failedDevices.map(({ name, reason }) => (
                                <div key={`fail-${name}`} className='bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 rounded-xl p-4 shadow-sm animate-in fade-in-0 duration-300'>
                                    <div className='flex items-center gap-3'>
                                        <div className='w-8 h-8 rounded-full bg-gradient-to-r from-rose-400 to-red-400 flex items-center justify-center'>
                                            <svg className='w-4 h-4 text-white' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                                <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v4m0 4h.01M10.29 3.86l-7.4 12.8A2 2 0 0 0 4.53 20h14.94a2 2 0 0 0 1.71-3.34l-7.4-12.8a2 2 0 0 0-3.49 0z' />
                                            </svg>
                                        </div>
                                        <div className='flex-1'>
                                            <div className='font-semibold text-rose-800'>Failed to update {name}</div>
                                            <div className='text-sm text-rose-700'>{reason}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="h-10">
                                <button
                                    onClick={onClearSelection}
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-all duration-200 group ${count === 0 ? 'invisible' : ''}`}
                                >
                                    <svg className="w-4 h-4 group-hover:rotate-12 transition-transform" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                    </svg>
                                    Clear Selection
                                </button>
                            </div>
                        </div>

                        {/* RIGHT: Update buttons */}
                        <div className='flex flex-col justify-center space-y-6 mb-20'>
                            {['proteuscore', 'ironman', ...(admin ? ['poseidon'] : [])].map((app, i) => {
                                const colors = [
                                    { bg: 'from-[#036AA9] to-[#025d94]', hover: 'hover:from-[#025d94] hover:to-[#014c7b]', accent: 'primary' },
                                    { bg: 'from-[#003759] to-[#002d49]', hover: 'hover:from-[#002d49] hover:to-[#001f34]', accent: 'dark' },
                                    { bg: 'from-[#57585B] to-[#3f4043]', hover: 'hover:from-[#3f4043] hover:to-[#2d2d2f]', accent: 'neutral' }
                                ][i];
                                
                                const appLabel = app === 'proteuscore' ? 'ProteusCore' : app === 'ironman' ? 'Configuration App' : 'Poseidon';
                                const isActive = activeApp === app;
                                
                                return (
                                    <div key={app} className='w-full max-w-md mx-auto space-y-4'>
                                        <button
                                            disabled={count === 0 || isSending}
                                            onClick={() => handleAppClick(app)}
                                            className={`w-full bg-gradient-to-r ${colors.bg} ${colors.hover} text-white px-4 py-3 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${colors.accent === 'primary' ? 'shadow-[#036AA9]/25' : colors.accent === 'dark' ? 'shadow-[#003759]/25' : 'shadow-[#57585B]/25'}`}
                                        >
                                            <span className='text-sm'>
                                                Update {appLabel}
                                            </span>
                                        </button>

                                        {isActive && (
                                            <div className='bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/60 shadow-lg space-y-4 animate-in slide-in-from-top-4 duration-300'>
                                                <div className='space-y-3'>
                                                    <div className='flex gap-3'>
                                                        <div className='flex-1'>
                                                            <label className='block text-sm font-medium text-slate-700 mb-2'>Release Channel</label>
                                                            <select
                                                                className='w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                                                                value={channel}
                                                                onChange={(e) => {
                                                                    setChannel(e.target.value);
                                                                    fetchAvailableVersions(app, e.target.value);
                                                                }}
                                                            >
                                                                {['Stable', 'Nightly', 'Experimental'].map((opt) => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className='flex-1'>
                                                            <label className='block text-sm font-medium text-slate-700 mb-2'>Version</label>
                                                            <select
                                                                className='w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                                                                value={selectedVersion}
                                                                onChange={(e) => setSelectedVersion(e.target.value)}
                                                            >
                                                                <option value='latest'>Latest version</option>
                                                                {availableUpdates.map((v, i) => (
                                                                    <option key={i} value={v}>{v}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='flex gap-3 pt-2'>
                                                    <button
                                                        onClick={() => sendBatchUpdate(app)}
                                                        disabled={count === 0 || isSending}
                                                        className={`flex-1 px-6 py-3 rounded-xl bg-gradient-to-r ${colors.bg} ${colors.hover} text-white font-semibold transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg`}
                                                    >
                                                        {isSending ? (
                                                            <span className='flex items-center justify-center gap-2'>
                                                                <svg className='animate-spin h-5 w-5 text-white' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                                                    <circle cx='12' cy='12' r='10' className='opacity-25' />
                                                                    <path d='M4 12a8 8 0 018-8' className='opacity-75' />
                                                                </svg>
                                                                Updating...
                                                            </span>
                                                        ) : (
                                                            <span className='flex items-center justify-center gap-2'>
                                                                <svg className='w-5 h-5' viewBox='0 0 16 16' fill='currentColor'>
                                                                    <path d='M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z'/>
                                                                    <path d='M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z'/>
                                                                </svg>
                                                                Deploy Update
                                                            </span>
                                                        )}
                                                    </button>

                                                    <button
                                                        onClick={() => setActiveApp(null)}
                                                        className='flex-1 px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-all duration-200 transform hover:scale-[1.02]'
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Results popup (success / partial / failure) */}
            {showResults && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
                    <div className='bg-white w-full max-w-xl mx-4 rounded-3xl shadow-2xl p-8 space-y-6 animate-in zoom-in-90 duration-300'>
                        <div className='flex flex-col items-center text-center space-y-3'>
                            {overallStatus === 'success' && (
                                <div className='w-16 h-16 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30'>
                                    <svg className='w-8 h-8 text-white' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                        <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                                    </svg>
                                </div>
                            )}
                            {overallStatus === 'partial' && (
                                <div className='w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30'>
                                    <svg className='w-8 h-8 text-white' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                        <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v4m0 4h.01M10.29 3.86l-7.4 12.8A2 2 0 0 0 4.53 20h14.94a2 2 0 0 0 1.71-3.34l-7.4-12.8a2 2 0 0 0-3.49 0z' />
                                    </svg>
                                </div>
                            )}
                            {overallStatus === 'failure' && (
                                <div className='w-16 h-16 bg-gradient-to-r from-rose-500 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30'>
                                    <svg className='w-8 h-8 text-white' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
                                        <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                                    </svg>
                                </div>
                            )}

                            <div>
                                <h3 className='text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent'>
                                    {overallStatus === 'success' && 'Update Complete'}
                                    {overallStatus === 'partial' && 'Some Updates Failed'}
                                    {overallStatus === 'failure' && 'All Updates Failed'}
                                </h3>
                                <p className='text-slate-600 font-medium mt-1'>
                                    {updatedDevices.length} succeeded • {failedDevices.length} failed
                                </p>
                            </div>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <div className='border border-emerald-200 rounded-2xl p-4 bg-emerald-50/50'>
                                <div className='flex items-center gap-2 mb-2'>
                                    <span className='w-2 h-2 rounded-full bg-emerald-500'></span>
                                    <div className='text-sm font-semibold text-emerald-800'>Succeeded</div>
                                </div>
                                {updatedDevices.length === 0 ? (
                                    <div className='text-sm text-emerald-800/70'>None</div>
                                ) : (
                                    <ul className='text-sm text-emerald-900 space-y-1 max-h-40 overflow-auto'>
                                        {updatedDevices.map((n) => <li key={`ok-${n}`} className='font-mono'>{n}</li>)}
                                    </ul>
                                )}
                            </div>

                            <div className='border border-rose-200 rounded-2xl p-4 bg-rose-50/50'>
                                <div className='flex items-center gap-2 mb-2'>
                                    <span className='w-2 h-2 rounded-full bg-rose-500'></span>
                                    <div className='text-sm font-semibold text-rose-800'>Failed</div>
                                </div>
                                {failedDevices.length === 0 ? (
                                    <div className='text-sm text-rose-800/70'>None</div>
                                ) : (
                                    <ul className='text-sm text-rose-900 space-y-1 max-h-40 overflow-auto'>
                                        {failedDevices.map(({ name, reason }) => (
                                            <li key={`fail-sum-${name}`} className='font-mono'>
                                                {name} — <span className='italic'>{reason}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className='flex gap-3'>
                            <button
                                onClick={() => { setShowResults(false); setUpdatedDevices([]); setFailedDevices([]); }}
                                className='w-full bg-gradient-to-r from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400 text-slate-800 font-semibold py-3 px-6 rounded-xl transition-all duration-200'
                            >
                                Close
                            </button>
                            <button
                                onClick={() => { onClearSelection(); setShowResults(false); setUpdatedDevices([]); setFailedDevices([]); }}
                                className='w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200'
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BatchUpdatePanel;